import { ContentWrapper, DataTable } from "@components/index";
import { Button, Modal, Pagination, Space, Spin, TableColumnsType, Typography } from "antd";
import { useEffect, useState } from "react";
import { get, remove } from "@services/api.service";
import { useNotification } from "@hooks/useNotification";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMaskId } from "~/Hooks/useMaskId";
import { useHasAccess } from "@hooks/useHasAccess";
import { Icon } from "@iconify/react";
import styles from "../Contract.module.scss";
import { useQuery } from "~/Hooks/useQuery";
import { debounce } from "lodash";
import { useAuth } from "@hooks/useAuth";
import { UserType } from "~/Utils";
import { getFormattedDate, getKey } from "@utils/CommonUtils";
import { FilterDropDown } from "~/Components/FilterDropDown/FilterDropDown";

export const PCDWatchers = () => {
  const [data, setData] = useState<any>([]);
  const { hasPermissions, hasRole } = useHasAccess();
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const { openToast, openNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setpage] = useState<number>(0);
  const [orderByName, setOrderByName] = useState("createdOn");
  const navigate = useNavigate();
  const { mask } = useMaskId();
  const { user } = useAuth();
  const [searchString, setSearchString] = useState("");
  const [uniqueId, setUniqueId] = useState<any>("");
  const query = useQuery();
  const [apiUrl, setApiUrl] = useState<any>("");
  const [open, setOpen] = useState(false);
  const [pageStart, setPageStart] = useState<number>(parseInt(query.get("page") || "0") || 0);

  useEffect(() => {
    if (hasRole(["*"])) {
      if (query.get("page")) {
        navigate(`/pcd-watchers?page=${pageStart}`, {
          replace: true,
        });
      }
    }
  }, [pageStart]);
  const menuClick = (key: string, record: { [key in string]: any }) => {
    if (key === "delete") {
      setOpen(true);
      setUniqueId(record?.uniqueId);
    }
    if (key === "edit") {
      navigate(`/pcd-watchers/edit/?uniqueId=${record.uniqueId}`);
    }
  };

  const handleDelete = () => {
    setIsLoading(true);
    remove(`v1/spa/pcd-watcher/delete-watcher?uniqueId=${uniqueId}`)
      .then((res: any) => {
        getRecords(sortOrder, orderByName, searchString, pageStart);
        setIsLoading(false);
        openNotification({
          title: "Success",
          message: res.message,
          type: "success",
        });
        setOpen(false);
      })
      .catch((err: any) => {
        setIsLoading(false);
        openNotification({
          title: err,
          message: err.message,
          type: "error",
        });
        setOpen(false);
      });
  };
  const tableActions = {
    callback: menuClick,
    items: [
      {
        label: (
          <Typography.Text
            type="secondary"
            style={{ color: styles.primary }}
          >
            <Space>
              <Icon
                icon="akar-icons:edit"
                inline
                fontSize={16}
                style={{ color: styles.primary }}
              />
              Edit
            </Space>
          </Typography.Text>
        ),
        key: "edit",
      },
      {
        label: (
          <Typography.Text type="danger">
            <Space>
              <Icon
                icon="fluent:delete-48-regular"
                inline
                fontSize={16}
              />
              Delete
            </Space>
          </Typography.Text>
        ),
        key: "delete",
      },
    ],
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    setIsLoading(true);
    const url = `v1/spa/pcd-watcher/get-watcher-list?size=${recordsPerPage}&page=${currentPage}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${encodeURIComponent(
      searchString
    )}`;
    setApiUrl(url);
    get(url)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData.records && responseData.records.length > 0) {
            setTotalRecords(responseData.totalCount || 0);
            setData(responseData.records);
          } else {
            console.error("No records found in the response");
            setData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting PCD Watchers failed",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, searchString.length > 0 ? 0 : pageStart);
  }, [searchString]);

  const columns: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "userName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "userName"
          ? "descend"
          : undefined,
    },
    {
      title: "User Email Id",
      dataIndex: "userEmail",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
    },
        {
      title: "Created Date",
      dataIndex: "createdOn",
      render: (value: any, record: any) =>
        value ? (
          <Typography.Text>{getFormattedDate(value)}</Typography.Text>
        ) : (
          <Typography.Text disabled>-</Typography.Text>
        ),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdOn"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdOn"
          ? "descend"
          : undefined,
    },
    {
      title: "Last Updated Date",
      dataIndex: "updatedOn",
      render: (value: any, record: any) =>
        value ? (
          <Typography.Text>{getFormattedDate(value)}</Typography.Text>
        ) : (
          <Typography.Text disabled>-</Typography.Text>
        ),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedOn"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedOn"
          ? "descend"
          : undefined,
    },
    {
      title: "Last Updated By",
      dataIndex: "updatedBy",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
    },
  ];

  const handleTableSort = (column: any, sortOrder: string, page: any) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getRecords(order, column.dataIndex, searchString, pageStart);
  };

  return (
    <ContentWrapper
      title={"PCD Watchers"}
      actionItems={
        <Button
          type="primary"
          onClick={() => navigate(`/pcd-watchers/create`)}
        >
          <Space>
            <Icon
              icon="akar-icons:plus"
              inline
            />
            Add
          </Space>
        </Button>
      }
    >
      {isLoading && (
        <div className="loadingOverlay">
          <Space size="middle">
            <Spin size="large" />
          </Space>
        </div>
      )}
      <DataTable
        columns={columns?.map((column) => ({
          ...column,
          onHeaderCell: (column: any) => ({
            onClick: () => {
              if (column.sorter) {
                handleTableSort(column, sortOrder, page ? page : 0);
              }
            },
          }),
        }))}
        className="table-clickable"
        tableData={
          data
            ? data?.map((d: any, index: number) => {
                return {
                  ...d,
                  key: getKey(page, index),
                };
              })
            : null
        }
        // {...{
        //   tableActions: tableActions,
        // }}
        tableActions={tableActions}
        isLoading={false}
        exportFileName="PCD Watchers"
        onClick={(record) => {
          if (record) {
            navigate(`/pcd-watchers?page=${pageStart}`)
            navigate(`/pcd-watchers/view/?uniqueId=${record.uniqueId}&view=${true}`);
          }
        }}
        onSearchChange={handleSearchChange}
        isServerSearchApi={true}
        inputDisable={isLoading}
        // additionalCTA={<FilterDropDown viewSubsidiary={true} viewStatus={false} setStatus={undefined}/>}
        isPagination={true}
        ctaColSpan={17}
        totalRecords={totalRecords}
        exportApiUrl={apiUrl}
        recordsPerPage={recordsPerPage}
        hideExport={false}
        searchPlaceholderText={"Search User"}
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={pageStart}
          total={data && totalRecords}
          current={pageStart + 1}
          onChange={(page) => {
            const startIndex = page - 1;
            setpage(page - 1);
            setPageStart(startIndex);
            getRecords(sortOrder, orderByName, searchString, startIndex);
          }}
          showSizeChanger={false}
        />
      </div>
      <Modal
        open={open}
        title={"Delete PCD Watcher"}
        centered
        closable={false}
        onCancel={() => setOpen(false)}
        maskClosable={false}
        okText={"Delete"}
        onOk={handleDelete}
        okButtonProps={{
          loading: isLoading,
        }}
      >
        <Typography.Text>Are you sure you want to delete this PCD Watcher?</Typography.Text>
      </Modal>
    </ContentWrapper>
  );
};
